import logger from '3-foundation/Logging/logger';
import { queryCDA } from '3-foundation/CDA/queryCDA';
import { mapFooterContent } from './Mappers/PageFooter/footerMapper';
import { mapPageProperties } from './Mappers/pagePropertiesMapper';
export const migrationPageIndicator = 'Synced';
export const getPageContentFromCDA = async ({
	siteId,
	site,
	locale,
	path,
	preview,
}: {
	siteId: string;
	site: Site.Site;
	locale: string;
	path: string;
	preview: boolean;
}): Promise<Content.PageContent> => {
	const profiler = logger.startTimer();

	const loggingName = 'getPageContent';
	try {
		path = path.startsWith('/') ? path : '/' + path;

		const pageData = await queryCDA<Umbraco.TreeNode>({
			siteId,
			locale,
			url: 'umbraco/delivery/api/v2/content/item' + (path === '/' ? '' : path),
			urlParams: {
				expand: 'properties[$all[properties[$all[properties[$all]]]]]',
			},
			preview,
			logging: loggingName,
		});

		const { contentType, name, id, route, properties, cultures } = pageData ?? {};
		const { items: ancestors } = await queryCDA<{ items: Umbraco.TreeNode[] }>({
			siteId,
			locale,
			url: 'umbraco/delivery/api/v2/content',
			urlParams: {
				fetch: `ancestors:${path}`,
				fields: 'properties[pageFooter,socialLinks,footerImage,footerImageAltText,bottomLinks]',
				expand: 'properties[$all]',
				sort: 'level:desc',
			},
			preview,
			logging: loggingName + 'Ancestors',
		});

		const getInheritedValue = (key: string) => {
			return properties?.[key] ?? ancestors.find((ancestor) => ancestor?.properties?.[key])?.properties?.[key];
		};

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const mapFooter = (pageData: any): Content.FooterContent => {
			let { footerImage, footerImageAltText, pageFooter, socialLinks, bottomLinks } = pageData?.properties ?? {};
			if (!footerImage || footerImage.length === 0) footerImage = getInheritedValue('footerImage') ?? [];
			if (!footerImageAltText) footerImageAltText = getInheritedValue('footerImageAltText') ?? '';
			if (!pageFooter) pageFooter = getInheritedValue('pageFooter');
			if (!socialLinks) socialLinks = getInheritedValue('socialLinks');
			if (!bottomLinks) bottomLinks = getInheritedValue('bottomLinks');

			return mapFooterContent({
				image: footerImage,
				imageAltText: footerImageAltText,
				footerCardLists: (pageFooter?.items ?? []).map((item: { content: Content.FooterCardList }) => item.content),
				socialLinkItems: socialLinks?.items ?? [],
				bottomLinkItems: bottomLinks ?? [],
			});
		};
		const documentType = contentType?.replace(migrationPageIndicator, '');
		const pageContent: Content.PageContent = {
			page: {
				documentType: documentType,
				culture: locale,
				id,
				parentId: ancestors?.[0]?.id || null,
				name,
				url: route?.path,
				route: route,
				properties: await mapPageProperties({ pageId: id, contentType: documentType, properties, siteId, site, locale }),
				cultures: Object.entries(cultures ?? {}).map(([locale, { path }]) => ({ [locale]: { url: path } })),
			},
			footer: mapFooter(pageData),
			preview: preview ?? null,
			previewData: null, //TODO: May not be needed when new preview plugin is implemented.
		};

		return pageContent;
	} catch (error) {
		logger.error('Could not get PageContent', { path, preview, error });
		throw new Error(`Could not get PageContent. \nCause: ${error.message}`, { cause: error });
	} finally {
		profiler.done({ message: loggingName, level: 'debug', path, siteId, locale });
	}
};
